/* eslint-disable react-refresh/only-export-components */
import {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
  createContext
} from "react";
import api from "api/landlord/auth";
import {
  DashboardPayload,
  getCurrentPartner,
  getDashboard,
  getPartnerDefaultEnrollments,
  PartnerDefaultEnrollmentsPayload
} from "api/landlord/landlord";
import { FEATURES, Partner as PartnerRes } from "types/landlord/partner";
import {
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types/partner";
import { useIntercom } from "react-use-intercom";
import { usePostHog } from "posthog-js/react";
import config from "config/config";
import { SupportedPropertyManagementSystem } from "utils/supportedPMS";
import { datadogRum } from "@datadog/browser-rum";

export enum SERVICE_TYPE {
  LANDLORD = "landlord",
  FULL_SERVICE = "full_service"
}

export enum ROLE {
  Admin = "admin",
  PropertyManager = "property_manager",
  Owner = "owner"
}

export enum BILLING_TYPE {
  SINGLE_ACCOUNT = "single_account",
  PER_PROPERTY = "per_property"
}

enum BILLING_FREQUENCY {
  MONTHLY = "monthly",
  WEEKLY = "weekly", // not used at the moment of 2025-08-08
  DAILY = "daily" // not used at the moment of 2025-08-08
}

/**
 * @description PMSes allowed to create enrollments, units and properties on BoomReport,
 * overriding the default behavior which restricts enrollment
 * though Boom platform, and only allows PMSes to enroll customers via
 * their own platform.
 */
const DirectBoomCreationPMSes = [PropertyManagementSystem.Appfolio];

type ColumnVisibilityItem = {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
};

type Address = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  lat: unknown;
  lng: unknown;
  postal_code: string;
  state_or_province: string;
};

type Property = {
  address: Address;
  can_delete: boolean;
  created_at: string;
  id: number;
  name: string;
  number_of_tenants: number;
  pms_id?: string;
};

type Authed = {
  id: number;
  obfuscated_id: string;
  email: string;
  name: string;
  partner: Partner;
  role: ROLE;
  can_change_available_by_date: boolean;
  can_change_rent_amount: boolean;
  can_make_decisions: boolean;
  can_view_reports: boolean;
  can_cancel_applications: boolean;
  can_change_unit: boolean;
  can_edit_income: boolean;
  can_manage_all_properties: boolean;
  can_override_decision_recommendation: boolean;
  can_show_full_ssn: boolean;
  can_change_applicant_role: boolean;
  can_subscribe_renters: boolean;
  can_edit_subscription_details: boolean;
  can_unsubscribe_renters: boolean;
  can_view_renters_list: boolean;
  can_view_rent_reporting: boolean;
  can_view_credit_reporting: boolean;
  can_apply_active_discounts: boolean;
  can_apply_custom_discounts: boolean;
  can_run_reports_manually: boolean;
  can_edit_application_rent: boolean;
  can_override_applicant_screening_template: boolean;
  can_access_screening_templates?: boolean;
  can_access_discount_codes?: boolean;
  boom_report_managers_allowed: boolean;
  can_access_reports?: {
    rental_application: boolean;
    identity_verification: boolean;
    income_report: boolean;
    housing_history_report: boolean;
    credit_report: boolean;
    us_criminal_report: boolean;
    ca_criminal_report: boolean;
    housing_court_report: boolean;
    animal_screening: boolean;
    disclosure: boolean;
    additional_documents: boolean;
  };
  properties: Property[];
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
  };
  crm_notification_settings?: {
    task_assigned: boolean;
    task_due_soon: boolean;
    mentioned_in_tasks: boolean;
  };
  stream_chat_token?: string;
  stream_chat_id?: string;
  columns_visibility?: Record<string, ColumnVisibilityItem[]>;
};

type Partner = {
  approved?: boolean;
  key_completed?: boolean;
  status: "active" | "started" | "rejected"; // verify if rejected status is a thing
  billing_completed?: boolean;
  billing_type: BILLING_TYPE;
  billing_period: BILLING_FREQUENCY;
  can_update_billing_type: boolean;
  access_key: string;
  email: string;
  name: string;
  properties: Property[];
  service_type: SERVICE_TYPE;
  url: string;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
  };
};

type Auth = undefined | null | Authed;

export type PartnerUserPermissions = {
  canChangeAvailablebyDate: boolean;
  canChangeRentAmount: boolean;
  canMakeDecisions: boolean;
  canViewReports: boolean;
  canManageProperties: boolean;
  canCancelApplications: boolean;
  canChangeUnit: boolean;
  canEditIncome: boolean;
  canOverrideDecisionRecommendation: boolean;
  canManageAllProperties: boolean;
  canShowFullSsn: boolean;
  canChangeApplicantRole: boolean;
  canUpdateBillingType: boolean;
  canSubscribeRenters: boolean;
  canEditSubscriptionDetails: boolean;
  canUnsubscribeRenters: boolean;
  canViewRentersList: boolean;
  canViewRentReporting: boolean;
  canViewCreditReporting: boolean;
  boomReportWriteAccess: boolean;
  canApplyActiveDiscounts: boolean;
  canApplyCustomDiscounts: boolean;
  canRunReportsManually: boolean;
  canEditApplicationRent: boolean;
  canOverrideApplicantScreeningTemplate?: boolean;
  canAccessScreeningTemplates: boolean;
  canManageDiscounts: boolean;
  canAccessReports: {
    rentalApplication: boolean;
    identityVerification: boolean;
    incomeReport: boolean;
    housingHistoryReport: boolean;
    creditReport: boolean;
    usCriminalReport: boolean;
    caCriminalReport: boolean;
    housingCourtReport: boolean;
    animalScreening: boolean;
    disclosure: boolean;
    additionalDocumentsReport: boolean;
  };
};

type Context = {
  userTimeZone?: string;

  auth: Auth;
  partner?: PartnerRes;
  dashboard?: DashboardPayload;
  partnerDefaultEnrollments?: PartnerDefaultEnrollmentsPayload;
  refreshPartnerData: () => Promise<
    [
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>
    ]
  >;
  refreshPartnerDefaultEnrollments: () => Promise<void>;
  refreshAuthUserDashboardState: () => void;
  refreshAuth: () => Promise<void>;
  partnerFeatures: PartnerFeatures[];
  type: {
    isManual: boolean;
    isSDK: boolean;
    isPMS: boolean;
    isBoomScreen: boolean;
    isBoomCRM: boolean;
    isPrescreeningIdv: boolean;
    isRentReporting: boolean;
    isBillPerProperty: boolean;
    isPropertyGroupMarket: boolean;

    isFullService: boolean;
    isLandlord: boolean;
    isBoomOps: boolean;
    adminPrivileges: boolean;
    isPropertyManager: boolean;
    isSitesAndCommunities: boolean;
    /**
     * @description Whether the partner can enroll customers, create units and properties through Boom, and not forced to use their own PMS platform for enrollments.
     */
    canManageEntitiesDirectly: boolean; // can enroll customers directly through Boom platform (if `isPMS` and `isRentReporting` are true),
    isShowFullSsn: boolean;
    isEnterprisePropertyware: boolean;
    isPmsListingSyncEnabled: boolean;
  };
  permissions: PartnerUserPermissions;
  pms: {
    isManageAmerica: boolean;
  };
};

const AppContext = createContext<Context>({
  auth: undefined,
  partner: undefined,
  dashboard: undefined,
  partnerDefaultEnrollments: undefined,
  refreshAuth: () => Promise.resolve(),
  refreshPartnerData: () =>
    Promise.allSettled([
      Promise.resolve(),
      Promise.resolve(),
      Promise.resolve()
    ]),
  refreshPartnerDefaultEnrollments: () => Promise.resolve(),
  refreshAuthUserDashboardState: () => {},
  partnerFeatures: [],
  type: {
    isManual: false,
    isSDK: false,
    isPMS: false,
    isBoomScreen: false,
    isPrescreeningIdv: false,
    isBoomCRM: false,
    isRentReporting: false,
    isBillPerProperty: false,
    isPropertyGroupMarket: false,

    isFullService: false,
    isLandlord: false,
    isBoomOps: false,
    isPropertyManager: false,
    isSitesAndCommunities: false,
    canManageEntitiesDirectly: false,
    isShowFullSsn: false,
    adminPrivileges: false,
    isEnterprisePropertyware: false,
    isPmsListingSyncEnabled: false
  },
  permissions: {
    canChangeAvailablebyDate: false,
    canChangeRentAmount: false,
    canMakeDecisions: false,
    canViewReports: false,
    canManageProperties: false,
    canChangeUnit: false,
    canEditIncome: true,
    canOverrideDecisionRecommendation: true,
    canShowFullSsn: false,
    canChangeApplicantRole: false,
    canUpdateBillingType: false,
    canSubscribeRenters: false,
    canEditSubscriptionDetails: false,
    canUnsubscribeRenters: false,
    canViewRentersList: false,
    canViewRentReporting: false,
    canViewCreditReporting: false,
    canCancelApplications: false,
    canManageAllProperties: false,
    boomReportWriteAccess: false,
    canApplyActiveDiscounts: false,
    canApplyCustomDiscounts: false,
    canRunReportsManually: false,
    canOverrideApplicantScreeningTemplate: false,
    canEditApplicationRent: false,
    canAccessScreeningTemplates: false,
    canManageDiscounts: false,
    canAccessReports: {
      rentalApplication: false,
      identityVerification: false,
      incomeReport: false,
      housingHistoryReport: false,
      creditReport: false,
      usCriminalReport: false,
      caCriminalReport: false,
      housingCourtReport: false,
      animalScreening: false,
      disclosure: false,
      additionalDocumentsReport: false
    }
  },
  pms: {
    isManageAmerica: false
  }
});

export const AuthProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  /**
   * Defines if there is an authed user
   * Null - if we could not determine auth (failed request etc.)
   * undefined - if we did not check for auth yet
   * Object - if auth is available!
   */
  const [auth, setAuth] = useState<Auth>(undefined);
  const [partner, setPartner] = useState<PartnerRes>();
  const [dashboard, setDashboard] = useState<DashboardPayload>();
  const [partnerDefaultEnrollments, setPartnerDefaultEnrollments] =
    useState<PartnerDefaultEnrollmentsPayload>();

  const posthog = usePostHog();
  const intercom = useIntercom();

  const refreshAuth = () => {
    return (
      api
        .checkAuth()
        // @ts-ignore
        .then((user: any) => {
          return refreshPartnerData().then(() => user);
        })
        .then((user) => {
          setAuth(user);
          posthog.reset();
          intercom.shutdown();
          if (user) {
            posthog.identify(user.id, {
              email: user.email,
              partner_id: user.partner?.id || "no_partner_id",
              property_management_system:
                user.partner?.property_management_system || "no_pms",
              partner_name: user.partner?.name || "no_partner_name",
              env: config.environment || "dev"
            });
            datadogRum.setUser({
              id: user.id,
              email: user.email,
              partner_id: user.partner?.id || "no_partner_id",
              partner_name: user.partner?.name || "no_partner_name",
              env: config.environment || "dev",
              name: user.name
            });
            intercom.boot({
              name: user.name,
              email: user.email,
              userId: user.intercom_id,
              customAttributes: {
                source: "partner_portal"
              },
              hideDefaultLauncher: true
            });
          } else
            intercom.boot({
              customAttributes: {
                source: "partner_portal"
              }
            });
        })
        .catch(() => {
          posthog.reset();
          intercom.shutdown();
          datadogRum.clearUser();
          setAuth(null);
          intercom.boot({
            customAttributes: {
              source: "partner_portal"
            }
          });
        })
    );
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  const refreshPartnerData = () => {
    return Promise.allSettled([
      getCurrentPartner()
        .then((partner) => setPartner(partner.partner))
        .catch(() => {
          // TODO: add error handling
        }),
      getDashboard()
        .then((dashboard) => setDashboard(dashboard))
        .catch(() => {
          // TODO: add error handling
        }),
      getPartnerDefaultEnrollments()
        .then((enrollments) => setPartnerDefaultEnrollments(enrollments))
        .catch(() => {
          // silence error
        })
    ]);
  };

  const refreshPartnerDefaultEnrollments = () => {
    return getPartnerDefaultEnrollments()
      .then((enrollments) => setPartnerDefaultEnrollments(enrollments))
      .catch(() => {
        // silence error
      });
  };

  const refreshAuthUserDashboardState = () => {
    api.checkAuth().then((user: Authed) => {
      setAuth(user);
    });
  };

  const isSDK = !!partner?.features.includes(FEATURES.SDK),
    isPMS =
      !!partner?.property_management_system?.name &&
      SupportedPropertyManagementSystem.includes(
        partner.property_management_system.name
      ),
    isManual = !isSDK && !isPMS,
    isBoomScreen = !!partner?.features.includes(FEATURES.BOOM_SCREEN),
    isBoomCRM = !!partner?.features.includes(FEATURES.BOOM_CRM),
    isPrescreeningIdv = !!partner?.features.includes(FEATURES.PRESCREENING_IDV),
    isRentReporting = !!partner?.features.includes(FEATURES.RENT_REPORTING),
    isBillPerProperty = partner?.billing_type === BILLING_TYPE.PER_PROPERTY,
    isPropertyGroupMarket = !!partner?.features.includes(
      FEATURES.PROPERTY_GROUP_MARKET
    ),
    isPropertyManager = auth?.role === ROLE.PropertyManager,
    isOwner = auth?.role === ROLE.Owner,
    isAdmin = auth?.role === ROLE.Admin,
    allowPropertyManagementFeature = partner?.features.includes(
      FEATURES.ALLOW_MANUAL_PROPERTY_MANAGEMENT
    ),
    isPmsListingSyncEnabled = partner?.features.includes(
      FEATURES.PMS_LISTING_SYNC
    ),
    canManageEntitiesDirectly =
      isPMS &&
      isRentReporting &&
      DirectBoomCreationPMSes.includes(
        (partner.property_management_system?.name ||
          "") as PropertyManagementSystem
      );

  const adminPrivileges = isAdmin || isOwner;

  const userTimeZone = "America/New_York" as string | undefined; // Replace with actual user time zone logic

  return (
    <AppContext.Provider
      value={{
        auth,
        userTimeZone:
          userTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        partner,
        refreshAuth,
        dashboard,
        partnerDefaultEnrollments,
        refreshPartnerData,
        refreshPartnerDefaultEnrollments,
        refreshAuthUserDashboardState,
        permissions: {
          canChangeAvailablebyDate:
            adminPrivileges || Boolean(auth?.can_change_available_by_date),
          canChangeRentAmount:
            adminPrivileges || Boolean(auth?.can_change_rent_amount),
          canMakeDecisions:
            adminPrivileges || Boolean(auth?.can_make_decisions),
          canViewReports: adminPrivileges || Boolean(auth?.can_view_reports),
          canUpdateBillingType:
            adminPrivileges || Boolean(partner?.can_update_billing_type),
          canManageProperties:
            (adminPrivileges && allowPropertyManagementFeature) ||
            canManageEntitiesDirectly,
          canCancelApplications:
            adminPrivileges || Boolean(auth?.can_cancel_applications),
          canChangeUnit: adminPrivileges || Boolean(auth?.can_change_unit),
          canOverrideDecisionRecommendation:
            adminPrivileges ||
            Boolean(auth?.can_override_decision_recommendation),
          canEditIncome: adminPrivileges || Boolean(auth?.can_edit_income),
          canManageAllProperties:
            adminPrivileges || Boolean(auth?.can_manage_all_properties),
          canShowFullSsn: adminPrivileges || Boolean(auth?.can_show_full_ssn),
          canChangeApplicantRole:
            adminPrivileges || Boolean(auth?.can_change_applicant_role),
          canSubscribeRenters:
            adminPrivileges || Boolean(auth?.can_subscribe_renters),
          canEditSubscriptionDetails:
            adminPrivileges || Boolean(auth?.can_edit_subscription_details),
          canUnsubscribeRenters:
            adminPrivileges || Boolean(auth?.can_unsubscribe_renters),
          canViewRentersList:
            adminPrivileges || Boolean(auth?.can_view_renters_list),
          canViewRentReporting:
            adminPrivileges || Boolean(auth?.can_view_rent_reporting),
          canViewCreditReporting:
            adminPrivileges || Boolean(auth?.can_view_credit_reporting),
          boomReportWriteAccess:
            adminPrivileges ||
            Boolean(
              auth?.can_subscribe_renters || auth?.can_edit_subscription_details
            ),
          canManageDiscounts:
            adminPrivileges || Boolean(auth?.can_access_discount_codes),
          canApplyActiveDiscounts:
            adminPrivileges || Boolean(auth?.can_apply_active_discounts),
          canApplyCustomDiscounts:
            adminPrivileges || Boolean(auth?.can_apply_custom_discounts),
          canRunReportsManually:
            adminPrivileges || Boolean(auth?.can_run_reports_manually),
          canEditApplicationRent:
            adminPrivileges || Boolean(auth?.can_edit_application_rent),
          canOverrideApplicantScreeningTemplate:
            adminPrivileges ||
            Boolean(auth?.can_override_applicant_screening_template),
          canAccessScreeningTemplates:
            adminPrivileges || Boolean(auth?.can_access_screening_templates),
          canAccessReports: {
            rentalApplication:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.rental_application),
            identityVerification:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.identity_verification),
            incomeReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.income_report),
            housingHistoryReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.housing_history_report),
            creditReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.credit_report),
            usCriminalReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.us_criminal_report),
            caCriminalReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.ca_criminal_report),
            housingCourtReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.housing_court_report),
            animalScreening:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.animal_screening),
            disclosure:
              adminPrivileges || Boolean(auth?.can_access_reports?.disclosure),
            additionalDocumentsReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.additional_documents)
          }
        },
        partnerFeatures: partner?.partner_features || [],
        type: {
          isManual,
          isSDK,
          isPMS,
          isBoomScreen,
          isBoomCRM,
          isPrescreeningIdv,
          isRentReporting,
          isPropertyManager,
          adminPrivileges,
          isBillPerProperty,
          isPropertyGroupMarket,

          isFullService: partner?.service_type === SERVICE_TYPE.FULL_SERVICE,
          isLandlord: partner?.service_type === SERVICE_TYPE.LANDLORD,
          isBoomOps: Boolean(String(auth?.email).includes("@boompay.app")),
          isSitesAndCommunities: Boolean(
            partner?.features.includes(FEATURES.SITES_AND_COMMUNITIES)
          ),
          canManageEntitiesDirectly,
          isShowFullSsn: Boolean(
            partner?.features.includes(FEATURES.SHOW_FULL_SSN)
          ),
          isEnterprisePropertyware: Boolean(
            partner?.features.includes(FEATURES.ENTERPRISE_PROPERTYWARE)
          ),
          isPmsListingSyncEnabled: Boolean(isPmsListingSyncEnabled)
        },
        pms: {
          isManageAmerica:
            partner?.property_management_system?.name ===
            PropertyManagementSystem.ManageAmerica
        }
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useAuthContext() {
  return useContext(AppContext);
}
